import { injectable } from 'tsyringe';
import { IUserActionServiceDelegate } from '../../../../shared/interfaces/user-action-service.interface';
import { LoggerService } from '../../../../shared/services/logger.service';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import { PersistentStorageService } from '../../persistent-storage-service/persistent-storage.service';
import { UserActionService } from '../../user-action.service';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import {
  ConditionType,
  conditionType,
} from './decrators/condition-type.decorator';
import { PromotionDisplayDetails } from '../../../../shared/models/promotion-display-details.model';
import { NoRecommendationReasons } from '../no-recommendations-reasons.enums';
import { Promotion } from '../../../models/promotion.model';
import { Game } from '../../../models/game.model';
import { PromotionType } from '../../datastore-service/models/raw-promotion.model';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Promotion)
export class ConditionPromotionPreviouslyDisplayed
  implements ICondition, IUserActionServiceDelegate
{
  private readonly logger = LoggerService.getCategory(
    'ConditionPromotionPreviouslyDisplayed',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly persistentLocalStorage: PersistentStorageService,
    private readonly userActionService: UserActionService,
  ) {
    this.userActionService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const { game, promotion } = context;

    const count = this.getApplicationDisplayedCount(promotion, game);

    if (!count || count === 0 || count < promotion?.displayLimit) {
      this.logger.info(
        `Previously displayed ${count}, out of ${promotion.displayLimit}`,
      );
      return [true];
    }

    this.logger.info(`Reached display limit of ${promotion.displayLimit}`);
    return [false, NoRecommendationReasons.PreviouslyDisplayed];
  }

  // ---------------------------------------------------------------------------
  private getApplicationDisplayedCount(
    promotion: Promotion,
    game: Game,
  ): number {
    const displayedApps =
      promotion?.type === PromotionType.Offer
        ? this.persistentLocalStorage.getDisplayedPromotionOfferInfo(game)
        : this.persistentLocalStorage.getDisplayedPromotionInfo(
            game,
            promotion,
          );

    if (!displayedApps) {
      return 0;
    }

    return displayedApps.count;
  }

  // ---------------------------------------------------------------------------
  // IUserActionServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onUserActionDisplayed(activePromotion: PromotionDisplayDetails) {
    const { promotionId, gameId, promotionType } = activePromotion;
    this.logger.info(
      `The promotion (${gameId}, ${promotionId}) was Displayed.`,
    );

    this.persistentLocalStorage.setPromotionDisplayedByType(
      gameId,
      promotionId,
      promotionType,
    );
  }
}
